import { SearchIcon, X } from 'lucide-react';
import { memo, MutableRefObject, useCallback, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useDebounceCallback, useOnClickOutside } from 'usehooks-ts';

import { ICON_SECONDARY } from '@shared/constants/colors';
import { ROUTES } from '@shared/constants/routes';
import { useAppSelector } from '@shared/hooks/redux';
import { FieldLabels } from '@shared/types/enums/fieldLabels.enum';
import { Button } from '@shared/ui/Button';
import { Input, InputProps } from '@shared/ui/Input';
import { getViewPort } from '@shared/ui/ScreenSizeTracking';
import { addPadding, removePadding } from '@shared/utils/scrollHelpers/scrollHelpers';
import { searchByProduct } from '@widgets/Header/api/searchByProduct/searchByProduct';
import { SearchProductItem } from '@widgets/Header/model/types/searchResponse';
import { getProductUrl } from '@widgets/ProductCard/utils/getProductUrl';

import cls from './Search.module.scss';

export const Search = memo(() => {
  const ref = useRef() as MutableRefObject<HTMLDivElement>;
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchList, setSearchList] = useState<SearchProductItem[]>([]);
  const [searchVisible, setSearchVisible] = useState(false);
  const requestIdRef = useRef(0);

  const { push } = useRouter();

  const viewPort = useAppSelector(getViewPort);

  const sendSearchValue = useCallback(
    async (value: string) => {
      const requestId = ++requestIdRef.current;

      if (value.length < 3) {
        setSearchVisible(false);
        return;
      }

      try {
        const res = await searchByProduct(value);
        if (requestId !== requestIdRef.current) return;

        if (res.length > 0) {
          setSearchList(res);

          if (viewPort !== 'desktop') {
            addPadding();
          }
        } else {
          setSearchList([]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (requestId === requestIdRef.current) {
          setSearchVisible(true);
        }
      }
    },
    [viewPort]
  );

  const onChangeHandler = (value: string) => {
    sendSearchDebounced(value);
    setSearchValue(value);
  };

  const handleClickEnter: InputProps['onKeyDown'] = (e) => {
    if (e.key === 'Enter' && searchValue) {
      closeSearchList();
      push(`${ROUTES.SEARCH}?q=${encodeURIComponent(searchValue)}`);
    }
  };

  const sendSearchDebounced = useDebounceCallback(sendSearchValue, 500);

  // Отменяем отложенный и текущий запросы, чтобы их ответ не открыл попап повторно
  const closeSearchList = () => {
    sendSearchDebounced.cancel();
    requestIdRef.current++;
    setSearchVisible(false);

    if (viewPort !== 'desktop') {
      removePadding();
    }
  };

  const onResetSearch = () => {
    setSearchValue('');
    setSearchList([]);
    closeSearchList();
  };

  useOnClickOutside(ref, onResetSearch);

  return (
    <div
      ref={ref}
      className={cls.wrap}
    >
      <div className={cls.field}>
        <Input
          variant={'default'}
          className={cls.input}
          value={searchValue}
          onChange={onChangeHandler}
          onKeyDown={handleClickEnter}
          placeholder={FieldLabels.SEARCH}
          label={FieldLabels.SEARCH}
          testId={'search-input'}
        />
        <Button
          aria-label={searchValue ? 'Стереть текст в поиске' : 'Найти товар'}
          size={'auto'}
          onClick={onResetSearch}
          className={cls.button}
          testId={'search-button'}
        >
          {searchValue ? <X size={16} /> : <SearchIcon size={16} />}
        </Button>
      </div>

      {searchVisible && (
        <div className={cls.result}>
          {searchList.length ? (
            <ul
              aria-label={'Результат поиска'}
              className={cls.list}
            >
              {searchList.map((el) => (
                <li
                  key={el.id}
                  className={cls.listItem}
                >
                  <Link
                    aria-label={`Перейти на страницу товара ${el.name}`}
                    href={getProductUrl(el.id, el.translitName)}
                    onClick={closeSearchList}
                    className={cls.item}
                  >
                    <span className={cls.icon}>
                      <SearchIcon
                        color={ICON_SECONDARY}
                        size={14}
                      />
                    </span>
                    <span className={cls.label}>{el.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className={cls.empty}>Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  );
});
